import { useMediaQuery } from '@abyss/web/hooks/useMediaQuery';
import { useRouter } from '@abyss/web/hooks/useRouter';
import { Button } from '@abyss/web/ui/Button';
import { Drawer } from '@abyss/web/ui/Drawer';
import { Fullscreen } from '@abyss/web/ui/Fullscreen';
import { Heading } from '@abyss/web/ui/Heading';
import { IconMaterial } from '@abyss/web/ui/IconMaterial';
import { Text } from '@abyss/web/ui/Text';
import { Fragment, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { Constants } from '../../common/Constants';
import { RecentActivityDrawer } from '../../common/RecentActivity/RecentActivityDrawer';
import { CountySearchContext } from '../../context/CountySearchContext';
import { PortalContext } from '../../context/PortalContext';
import { SearchLocation } from '../../frontends/ProviderSearch/context/SearchLocation';
import {
  ButtonStyles,
  IconStyles,
} from '../../frontends/ProviderSearch/routes/NullSpecialtyPage/NullSpecialtyPage.styles';
import { useDeviceLocation } from '../../hooks/useDeviceLocation';
import { useLagoon } from '../../hooks/useLagoon';
import { useGetRecentSearchesResults } from '../../hooks/useRecentSearches';
import { useTypeaheadStore } from '../../store/useTypeaheadStore';
import { TypeaheadState } from '../../store/useTypeaheadStore/typeaheadStore';
import { getCurrentMember, parseLOB } from '../../utils/user.utils';
import { CardHeader } from '../CardHeader/CardHeader';
import { cardHeaderNullPageStyle, phoneOnly } from '../ConstantsStyles';
import { LinkWithTracking } from '../LinkWithTracking/LinkWithTracking';
import { LocationSearchMobile } from '../PSXHeader/LocationSearchMobile/LocationSearchMobile';
import {
  getGeoLocationFromStorage,
  setGeoLocationToStorage,
} from '../PSXHeader/SearchBar/utils';
import { isCounty } from '../SnackCardContainer/utils';
import { getLagoonValueByKey } from '../Utils/getLagoonValueByKey';
import {
  NullResultSectionStyled,
  StyledUl,
  SubHeader,
  Wrapper,
} from './NullResultsPage.styles';

export type NullPageProps = {
  searchTitle: string;
  lagoonData: any;
  specialtySearch?: boolean;
};

const parseLagoonContent = (html: string) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const liElements = doc.querySelectorAll('li');
  const processedContent = Array.from(liElements).map((li) => {
    const span = li.querySelector('span');
    const placeholder = `{${span?.id}}`;
    span?.replaceWith(placeholder);
    return {
      text: li.textContent ? li.textContent.trim() : '',
      id: span?.id || null,
      buttontext: span?.textContent || null,
    };
  });
  return processedContent;
};

export const NoResult = ({
  searchTitle,
  lagoonData,
  specialtySearch,
}: NullPageProps) => {
  const { navigate } = useRouter();
  const { t } = useTranslation();
  const { setTypeaheadSearchStore } = useTypeaheadStore(
    useShallow((state: TypeaheadState) => ({
      setTypeaheadSearchStore: state.setTypeaheadSearchStore,
    }))
  );
  const currentMember = getCurrentMember();
  const lob = currentMember?.lineOfBusiness;
  const parsedLob = parseLOB(lob);
  const { portalData } = useContext(PortalContext);
  const isGuestUser = portalData?.portalConfig?.isGuestUser;
  const [isOpenLocationChange, setIsOpenLocationChange] = useState(false);
  const [, setDeviceLocation] = useState<string>(
    getGeoLocationFromStorage() ?? ''
  );
  const { setIsCountySearch } = useContext(CountySearchContext);

  const mobileScreen = useMediaQuery(phoneOnly);
  const headingKey = Constants.RECENT_ACTIVITY_HEADING_KEY['All'];
  const headingsLagoon = useLagoon('recent-searches')();
  const allCareDrawerHeading = getLagoonValueByKey(
    headingsLagoon,
    headingKey,
    'drawer'
  );
  const [isOpen, setIsOpen] = useState(false);

  const { data } = useGetRecentSearchesResults();

  const handleChangeLocation = () => {
    setIsOpenLocationChange(true);
  };
  const handleOnClickHistory = () => {
    setIsOpen(true);
  };
  const handleOnClickViewPlanOptions = () => {
    // Dispatch custom event for parent application to handle navigation
    const navigationEvent = new CustomEvent('guestNavigationRequest', {
      detail: {
        path: '/select-coverage-type',
        params: { planSelectionLob: parsedLob },
      },
      bubbles: true,
    });
    window.dispatchEvent(navigationEvent);
  };

  const handleBack = () => {
    setTypeaheadSearchStore({
      searchType: Constants.SEARCH_TYPES.NAME,
      previous: true,
    });
    navigate(-1);
  };

  const handleOnClose = () => {
    setIsOpenLocationChange(false);
  };

  const { getDeviceLocation } = useDeviceLocation(
    setDeviceLocation,
    setIsOpenLocationChange
  );
  const geoLocation = getGeoLocationFromStorage();

  /* The current Lagoon content is in markdown format, which requires
  using createPortal to inject a React component (Link) into the span element.
   This approach causes issues as it uses both React and plain JavaScript
   to manage the DOM, leading to errors when the user is on the NSR page and
   searches for a term that has results with a coverage type different from
   the one on the NSR page. To address this, we are using the parsed Lagoon
   content to render the content as a list of items with text and IDs.
   This approach allows us to render the content without relying on createPortal.
*/
  const parsedLagoonContent = lagoonData?.content
    ? parseLagoonContent(lagoonData.content)
    : [];

  const getLinkDataTestId = (id: string) => {
    switch (id) {
      case 'nullpage-history':
        return {
          testId: 'null-page-view-history',
          onClick: handleOnClickHistory,
        };
      case 'view-plan-options':
        return {
          testId: 'null-page-view-plan-options',
          onClick: handleOnClickViewPlanOptions,
        };
      case 'nullpage-location':
        return { testId: 'null-page-location', onClick: handleChangeLocation };
      default:
        return { testId: id, onClick: () => {} };
    }
  };

  const { name: locationName } = geoLocation;
  if (!lagoonData) return null;
  return (
    <Fragment>
      <NullResultSectionStyled>
        <Wrapper data-auto-testid="null-page" data-testid="null-page">
          <CardHeader
            css={cardHeaderNullPageStyle}
            data-auto-testid="browse-common-specialities-heading"
            data-testid="browse-common-specialities-heading"
            offset={3}
          >
            {lagoonData?.heading} {`'${searchTitle}'`}
          </CardHeader>
          <SubHeader
            color="$gray7"
            css={{
              '&.abyss-heading-root': {
                fontWeight: '$semibold',
                fontSize: '14.22px',
                lineHeight: '16px',
              },
            }}
            data-auto-testid="browse-common-specialities-sub-heading"
            data-testid="browse-common-specialities-sub-heading"
            offset={5}
          >
            {lagoonData.subHeading}
          </SubHeader>
          <StyledUl>
            {parsedLagoonContent?.map((item, index) => (
              <li key={index}>
                {!item?.id ? (
                  item?.text
                ) : (
                  <span>
                    {item?.text.split(`{${item?.id}}`)[0]}
                    <Text size="$sm">
                      <LinkWithTracking
                        css={{
                          lineHeight: '15px',
                        }}
                        data-auto-testid={`${
                          getLinkDataTestId(item?.id).testId
                        }`}
                        data-testid={`${getLinkDataTestId(item?.id).testId}`}
                        isStandardAnchor
                        onClick={getLinkDataTestId(item?.id).onClick}
                        size="14.22px"
                      >
                        {item?.buttontext}
                      </LinkWithTracking>
                    </Text>
                    {item?.text.split(`{${item?.id}}`)?.[1]}
                  </span>
                )}
              </li>
            ))}
          </StyledUl>
          {specialtySearch && !isGuestUser ? (
            <Button
              css={ButtonStyles}
              data-auto-testid="back-to-previous-page"
              data-testid="back-to-previous-page"
              onClick={handleBack}
              size="$md"
            >
              {t('NULL_PAGE.NULL_RESULTS_BACK_TO_PREVIOUS_PAGE_BUTTON')}
              <IconMaterial
                color="$white"
                css={IconStyles}
                icon="chevron_right"
                isScreenReadable
                size="24px"
              />
            </Button>
          ) : null}
        </Wrapper>
      </NullResultSectionStyled>
      {mobileScreen && isOpenLocationChange ? (
        <Drawer
          css={{
            'abyss-modal-close-button': {
              marginTop: '$md',
              marginRight: '8px',
            },
            'abyss-modal-header-container': {
              marginLeft: '$sm',
            },
            'abyss-modal-content-container': {
              borderRadius: '20px 20px 0px 0px',
            },
          }}
          isOpen={isOpenLocationChange}
          onClose={() => setIsOpenLocationChange(false)}
          position="bottom"
          size="$lg"
          title={
            <Heading color="$gray8" offset={4}>
              {!locationName ? t('My search location') : t('Location Change')}
            </Heading>
          }
        >
          <LocationSearchMobile
            findUserLocation={getDeviceLocation}
            location={locationName}
            setIsOpen={setIsOpenLocationChange}
            setLocation={(targetLocation) => {
              const {
                id,
                center = [],
                place_name: placeName,
                stateCode,
                zipCode,
              } = targetLocation;
              const [selectedLong, selectedLat] = center;
              if (isCounty(id)) setIsCountySearch(true);
              else setIsCountySearch(false);

              setGeoLocationToStorage({
                id,
                name: placeName,
                longitude: selectedLong,
                latitude: selectedLat,
                stateCode,
                zipCode,
              });
            }}
          />
        </Drawer>
      ) : null}
      {!mobileScreen && isOpenLocationChange ? (
        <Fullscreen
          isOpen={isOpenLocationChange}
          onClose={handleOnClose}
          title=""
        >
          <Fullscreen.Section>
            <SearchLocation
              handleOnClose={handleOnClose}
              isOpenLocationChange={isOpenLocationChange}
            />
          </Fullscreen.Section>
        </Fullscreen>
      ) : null}
      {data?.recentSearches ? (
        <div data-testid="recent-search">
          <RecentActivityDrawer
            drawerHeading={allCareDrawerHeading}
            isOpen={isOpen}
            recentSearchesResponse={data?.recentSearches}
            setIsOpen={setIsOpen}
          />
        </div>
      ) : null}
    </Fragment>
  );
};
